/**
 * useSession — tracks the current Supabase session and the matching app_users
 * row (tenant_id, tier, admin flags). Used by surfaces that need to know who
 * the caller is and what they're allowed to see.
 *
 * RE-LOCKED 2026-05-03: when a Supabase session is present but no app_users
 * row exists yet (the post-signup, pre-finalize state), this hook calls
 * `/api/signup-finalize` to create the row, then re-fetches. This replaces
 * the dropped `on_auth_user_created` trigger from migration 0001.
 *
 * BUGFIX 2026-05-03 (V1 staging follow-up #10 + #11):
 * Two failure modes resolved in this revision:
 *
 *   (a) Supabase auth-lock deadlock. Modern @supabase/supabase-js (v2.45+)
 *       holds the auth lock for the duration of `onAuthStateChange` callback
 *       execution. When that callback awaits `supabase.from(...).select()`
 *       OR `supabase.auth.*` calls, those calls wait for the lock that's
 *       held by the dispatcher → deadlock. Symptoms: ProfileList stuck on
 *       Loading because loadAppUser never returns; SignIn submit fires zero
 *       network requests because supabase.auth.signInWithOtp can't acquire
 *       the auth lock. Fix: keep the onAuthStateChange callback synchronous;
 *       defer all data-loading via `queueMicrotask` so the lock releases
 *       before any supabase API runs.
 *
 *   (b) `setLoading(false)` was downstream of `await loadAppUser(...)` in
 *       the getSession path. If loadAppUser hung (deadlock above) or
 *       threw, loading stayed true forever, even after session was set.
 *       Fix: setLoading(false) fires immediately after we know whether a
 *       session exists; loadAppUser runs after that, deferred.
 *
 *   (c) Per-instance finalize Set didn't dedupe across the 18 useSession
 *       call sites. Fix: hoist the in-flight Set to module scope so two
 *       components mounted on the same page coalesce into one fetch.
 */

import { useEffect, useState } from "react";
import { supabase, getActiveBearerToken } from "./supabase.js";
import { getImpersonationToken } from "./impersonation-client.js";

// Module-scoped finalize coalescing. Two components calling useSession on
// the same page won't fire two `/api/signup-finalize` requests; the second
// one awaits the first one's in-flight promise.
const inflightFinalize = new Map(); // user_id → Promise

async function finalizeOnce(userId, accessToken) {
  if (inflightFinalize.has(userId)) {
    return inflightFinalize.get(userId);
  }
  const p = (async () => {
    try {
      const res = await fetch("/.netlify/functions/signup-finalize", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
        body: "{}",
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      // Allow one re-attempt later if needed (e.g. network blip on first try).
      // For the common case it stays cached just long enough to dedupe the
      // two-component race.
      setTimeout(() => inflightFinalize.delete(userId), 5_000);
    }
  })();
  inflightFinalize.set(userId, p);
  return p;
}

async function fetchAppUser(userId) {
  // Phase 1.6 Bug 1 fix (2026-05-12): during impersonation, route through
  // /api/me/app-user instead of direct supabase.from() — the SDK uses its
  // OWN internal token for direct REST calls, which bypasses our
  // getSession() wrap and returns the SUPERADMIN's app_users row (not the
  // impersonated target's). The endpoint runs through requireAuth which
  // detects the imp token via the pp_imp_ prefix and builds the target
  // user's context. Result shape matches the direct-from() shape so this
  // is a drop-in replacement for the consumer.
  if (getImpersonationToken()) {
    try {
      const token = await getActiveBearerToken();
      const res = await fetch("/.netlify/functions/me-app-user", {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  // Phase 1.5 (2026-05-12): include the related tenants row
  // (tenant_status + display_name + slug) so customer-side surfaces can
  // render the ArchivedTenantBanner without a second roundtrip. Same shape
  // as `requireAuth` on the server side; one round-trip via the FK JOIN.
  const { data } = await supabase
    .from("app_users")
    .select("user_id, tenant_id, tier, is_tenant_admin, is_hr_admin, is_superadmin, display_name, role_label, operator_context, tenants(tenant_status, display_name, slug)")
    .eq("user_id", userId)
    .single();
  return data || null;
}

export function useSession() {
  const [session, setSession] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizeAttempted, setFinalizeAttempted] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadAppUser(s) {
      if (!active || !s?.user?.id) {
        if (active) setAppUser(null);
        return;
      }

      const existing = await fetchAppUser(s.user.id);
      if (!active) return;

      if (existing) {
        setAppUser(existing);
        return;
      }

      // No row yet — finalize, then refetch. finalizeOnce dedupes across
      // hook instances on the same page.
      const ok = await finalizeOnce(s.user.id, s.access_token);
      if (!active) return;
      setFinalizeAttempted(true);

      if (ok) {
        const refetched = await fetchAppUser(s.user.id);
        if (active) setAppUser(refetched);
      } else {
        setAppUser(null);
      }
    }

    // Initial session read. Synchronous-style: set session + loading=false
    // first, defer the loadAppUser via microtask so we don't hold the auth
    // lock during it. This unblocks ProfileList and other consumers that
    // gate on `loading` immediately.
    supabase.auth.getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setLoading(false);
        if (data.session) {
          queueMicrotask(() => { loadAppUser(data.session); });
        } else {
          setAppUser(null);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    // Auth state change subscription. Per Supabase v2.45+ docs, this callback
    // MUST stay synchronous — the auth lock is held during its execution and
    // any `supabase.from()` or `supabase.auth.*` await inside will deadlock.
    // We update React state synchronously and defer data-loading.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!active) return;
      setSession(newSession);
      if (newSession) {
        queueMicrotask(() => { loadAppUser(newSession); });
      } else {
        setAppUser(null);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Cold-signin guard 2026-05-04: a Supabase session that has no associated
  // app_users row AND can't be finalized (signup-finalize ran but couldn't
  // resolve a tenant_id from app_metadata / user_metadata / JWT claim) is
  // a "cold sign-in" — user signed in but isn't actually provisioned for
  // any tenant. The previous failure mode was a raw 403 "User has no
  // PeoplePrints app_users row" exception bubbling from requireAuth on
  // the next API call. The new contract: callers can read needsOnboarding
  // and route to /onboarding/no-tenant before triggering any backend call.
  const needsOnboarding = !loading && !!session && !appUser && finalizeAttempted;

  // Phase 1.5 (2026-05-12): tenant-status convenience fields derived from
  // the JOIN. Customer-side surfaces gate UX on these (ArchivedTenantBanner
  // renders when tenantArchived; future suspended-tenant banner could use
  // tenantStatus directly).
  const tenantRow = appUser?.tenants || null;
  const tenantStatus = tenantRow?.tenant_status || (appUser ? "active" : null);
  const tenantArchived = tenantStatus === "archived";
  const tenantDisplayName = tenantRow?.display_name || null;
  const tenantSlug = tenantRow?.slug || null;

  return {
    session,
    appUser,
    loading,
    needsOnboarding,
    tenantStatus,
    tenantArchived,
    tenantDisplayName,
    tenantSlug,
  };
}
